import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { UNIVERSES } from "@/data/universes";
import { SEO_PAGES } from "@/data/seoProPages";
import { OrderContact } from "@/components/OrderContact";
import { CtaButtons, ProductTile, useProProducts } from "@/components/home/shared";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";

export default function UniversePage({ u }) {
  const products = useProProducts();
  useSeo({ title: u.seoTitle, description: u.seoDesc, path: u.path, image: `${SITE_URL}${u.image}` });
  useJsonLd("ld-breadcrumb", {
    "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: u.title, item: `${SITE_URL}${u.path}` }],
  });
  const list = (products || []).filter(u.match || ((p) => p.universes?.includes(u.key)));
  const catalog = UNIVERSES.includes(u) ? `/espace-pro?univers=${u.key}` : "/espace-pro";
  const others = [...UNIVERSES, ...SEO_PAGES].filter((x) => x.key !== u.key);
  return (
    <div data-testid={`universe-page-${u.key}`}>
      <section className="relative bg-[var(--oz-dark)] text-white overflow-hidden">
        <img src={u.image} alt={u.alt} width="800" height="800" className="absolute inset-y-0 right-0 w-full md:w-1/2 h-full object-cover opacity-40 md:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--oz-dark)] via-[var(--oz-dark)]/90 md:via-[var(--oz-dark)] to-transparent md:to-[var(--oz-dark)]/10" />
        <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24">
          <nav aria-label="Fil d'Ariane" className="text-xs text-white/60"><Link to="/" className="hover:text-white">Accueil</Link> / {u.title}</nav>
          <h1 data-testid="universe-title" className="mt-4 max-w-xl font-serif text-4xl sm:text-5xl leading-tight">{u.h1}</h1>
          <div className="mt-6 max-w-xl space-y-3 text-white/80 leading-relaxed">{u.intro.map((t) => <p key={t}>{t}</p>)}</div>
          <div className="mt-8"><CtaButtons dark testid="universe" secondary={{ to: catalog, label: "Découvrir le catalogue professionnel" }} /></div>
        </div>
      </section>
      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-14 grid md:grid-cols-3 gap-6">
        {u.points.map(([t, d]) => <div key={t}><h2 className="font-semibold text-lg">{t}</h2><p className="mt-2 text-sm text-[var(--oz-muted)] leading-relaxed">{d}</p></div>)}
      </section>
      {list.length > 0 && <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-16">
        <h2 className="font-serif text-3xl">Les produits concernés</h2>
        <div data-testid="universe-products" className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">{list.map((p) => <ProductTile key={p.id} p={p} />)}</div>
        <p className="mt-6 text-sm text-[var(--oz-muted)]">Prix HT réservés aux comptes professionnels validés. Livraison offerte en France métropolitaine ; DOM-TOM, Belgique et Europe à tarif fixe.</p>
      </section>}
      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-14">
        <OrderContact title="Demander des informations professionnelles" testid="seo-page-contact" />
      </section>
      <section className="bg-[var(--oz-bg-2)]">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-14 flex flex-col md:flex-row gap-6 md:items-center justify-between">
          <div className="flex flex-wrap gap-3">
            {others.map((x) => <Link key={x.key} to={x.path} data-testid={`universe-link-${x.key}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--oz-border)] px-4 py-2 text-sm hover:border-[var(--oz-gold)]">{x.title} <ArrowRight className="w-3.5 h-3.5" /></Link>)}
            <Link to="/avantages-professionnels" className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--oz-border)] px-4 py-2 text-sm hover:border-[var(--oz-gold)]">Avantages professionnels <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
